import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import {
  Firestore,
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryConstraint,
  serverTimestamp,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from '@angular/fire/storage';
import { BehaviorSubject, filter, firstValueFrom, take } from 'rxjs';
import { Observable } from 'rxjs/internal/Observable';
import { Exercise, ExerciseHistory } from 'src/models/exercise.model';
import { ErrorService } from './error.service';
import { DbExercise } from 'src/models/db-exercises.model';

/** Statistics/history ownership is always supplied explicitly. */
export type StatisticsScope =
  | { type: 'all' }
  | { type: 'mesocycle'; mesocycleId: string }
  | { type: 'unassigned' };

export interface StatisticsDateRange {
  from?: Date;
  to?: Date;
}

export interface ExerciseHistoryQueryOptions {
  userId: string;
  scope: StatisticsScope;
  dateRange?: StatisticsDateRange;
}

/** Exported for consumers which combine already-loaded history locally. */
export function matchesStatisticsScope(
  history: ExerciseHistory,
  scope: StatisticsScope
): boolean {
  switch (scope.type) {
    case 'all':
      return true;
    case 'mesocycle':
      return history.mesocycleId === scope.mesocycleId;
    case 'unassigned':
      // Missing is the legacy representation; tolerate explicit null data too.
      return history.mesocycleId == null;
  }
}

@Injectable({ providedIn: 'root' })
export class ExerciseService {
  private exerciseMap: Map<string, Exercise> = new Map();

  constructor(
    private firestore: Firestore,
    private storage: Storage,
    private http: HttpClient,
    private errorService: ErrorService,
    private auth: Auth
  ) {}

  async getExerciseById(id: string): Promise<Exercise | undefined> {
    try {
      // Check if the exercise is already in the map
      if (this.exerciseMap.has(id)) {
        return this.exerciseMap.get(id);
      } else {
        // Fetch the exercise from Firestore if it's not in the map
        const exerciseDocRef = doc(this.firestore, `exercises/${id}`);
        const docSnap = await getDoc(exerciseDocRef);

        if (docSnap.exists()) {
          const exerciseData = {
            id: docSnap.id,
            ...(docSnap.data() as Exercise),
          };
          // Store the fetched exercise in the map
          this.exerciseMap.set(id, exerciseData);
          return exerciseData;
        } else {
          // Handle the case where the exercise does not exist in Firestore
          return undefined;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        // Utilize the error message from the caught exception
        this.errorService.showError(
          `Failed to get exercise with ID ${id}: ${error.message}`
        );
      } else {
        // Generic error message for non-Error instances
        this.errorService.showError(
          `Failed to get exercise with ID ${id} due to an unexpected error`
        );
      }
      return undefined; // Return undefined in case of error to maintain function return type consistency
    }
  }

  async getExercise(exerciseId: string): Promise<Exercise | undefined> {
    try {
      // Check if the exercise is already in the map
      if (this.exerciseMap.has(exerciseId)) {
        return this.exerciseMap.get(exerciseId);
      } else {
        // Fetch the exercise from Firestore if it's not in the map
        const exerciseDocRef = doc(this.firestore, `exercises/${exerciseId}`);
        const docSnap = await getDoc(exerciseDocRef);

        if (docSnap.exists()) {
          const exerciseData = {
            id: docSnap.id,
            ...(docSnap.data() as Exercise),
          };
          // Store the fetched exercise in the map
          this.exerciseMap.set(exerciseId, exerciseData);
          return exerciseData;
        } else {
          // Handle the case where the exercise does not exist in Firestore
          return undefined;
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        // Utilize the error message from the caught exception
        this.errorService.showError(
          `Failed to get exercise with ID ${exerciseId}: ${error.message}`
        );
      } else {
        // Generic error message for non-Error instances
        this.errorService.showError(
          `Failed to get exercise with ID ${exerciseId} due to an unexpected error`
        );
      }
      return undefined; // Return undefined in case of error to maintain function return type consistency
    }
  }

  async addExercise(exercise: Exercise) {
    const exercisesRef = collection(this.firestore, 'exercises');

    // Augment the exercise object with createdAt and updatedAt timestamps
    const exerciseWithTimestamps = {
      ...exercise,
      createdAt: serverTimestamp(), // Sets the timestamp to the server's current time
      updatedAt: serverTimestamp(), // Initially, updatedAt will be the same as createdAt
    };

    return await addDoc(exercisesRef, exerciseWithTimestamps);
  }

  async uploadImage(file: File, userId: string): Promise<string> {
    const filePath = `exerciseImages/${userId}/${new Date().getTime()}_${
      file.name
    }`;
    const fileRef = ref(this.storage, filePath);

    await uploadBytes(fileRef, file);
    return getDownloadURL(fileRef);
  }

  async uploadImageFromUrl(imageUrl: string, userId: string): Promise<string> {
    const response$ = this.http.get(imageUrl, { responseType: 'blob' });
    const blob = await firstValueFrom(response$); // Use firstValueFrom instead of toPromise

    const fileName = `gif_${new Date().getTime()}.gif`;
    const filePath = `exerciseImages/${userId}/${fileName}`;
    const fileRef = ref(this.storage, filePath);

    await uploadBytes(fileRef, blob);
    return getDownloadURL(fileRef);
  }

  getUserExercises(userId: string): Observable<Exercise[]> {
    const exercisesSubject = new BehaviorSubject<Exercise[]>([]);

    const exercisesRef = collection(this.firestore, 'exercises');
    // Order the exercises by the 'createdAt' field in descending order
    const q = query(
      exercisesRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const exercises: Exercise[] = [];
        snapshot.forEach((doc) => {
          const exerciseWithId = { id: doc.id, ...(doc.data() as Exercise) }; // Combine the document ID with the exercise data
          exercises.push(exerciseWithId);
        });
        exercisesSubject.next(exercises); // Emit the ordered list of exercises
      },
      (error) => {
        this.errorService.showError(
          `Error fetching exercises: ${error.message}`
        );
        exercisesSubject.error(error); // Notify subscribers of the error
      }
    );

    // Return the observable with an additional cleanup logic for the subscription
    return new Observable<Exercise[]>((subscriber) => {
      const subscription = exercisesSubject.subscribe(subscriber);
      return () => {
        unsubscribe(); // Ensure we unsubscribe from Firestore updates when the Observable is unsubscribed from
      };
    });
  }

  getUserExercisesOnce(userId: string): Observable<Exercise[]> {
    const exercisesRef = collection(this.firestore, 'exercises');
    const q = query(
      exercisesRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    return new Observable<Exercise[]>((subscriber) => {
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const exercises: Exercise[] = [];
          snapshot.forEach((doc) => {
            const exerciseWithId = { id: doc.id, ...(doc.data() as Exercise) };
            exercises.push(exerciseWithId);
          });
          subscriber.next(exercises);
          subscriber.complete(); // Complete the observable after the first fetch
          unsubscribe(); // Clean up the listener
        },
        (error) => {
          this.errorService.showError(
            `Error fetching exercises: ${error.message}`
          );
          subscriber.error(error); // Notify subscribers of the error
        }
      );

      // Return a cleanup function
      return () => {
        unsubscribe(); // Ensure we unsubscribe from Firestore updates when the Observable is unsubscribed from
      };
    });
  }

  async removeExercise(exercise: Exercise) {
    try {
      const exerciseDocRef = doc(this.firestore, `exercises/${exercise.id}`);
      await deleteDoc(exerciseDocRef);

      if (exercise.imageUrl) {
        const imagePath = this.getImagePathFromUrl(exercise.imageUrl);
        const imageRef = ref(this.storage, imagePath);
        await deleteObject(imageRef);
      }
    } catch (error) {
      if (error instanceof Error) {
        this.errorService.showError(
          `Failed to remove exercise: ${error.message}`
        );
      } else {
        this.errorService.showError(
          'Failed to remove exercise due to an unexpected error'
        );
      }
    }
  }

  private getImagePathFromUrl(url: string): string {
    const urlParts = new URL(url);
    // Decode the URL path and split it into parts
    const decodedPath = decodeURIComponent(urlParts.pathname);

    // The correct path should start after '/o/', so find that and extract the subsequent part
    const pathStartIndex = decodedPath.indexOf('/o/') + 3; // +3 to skip '/o/'
    let filePath = decodedPath.substring(pathStartIndex);

    // Remove any query parameters that might be appended to the path
    const queryParamIndex = filePath.indexOf('?');
    if (queryParamIndex !== -1) {
      filePath = filePath.substring(0, queryParamIndex);
    }

    return filePath;
  }

  async updateExercise(exerciseId: string, exercise: Partial<Exercise>) {
    try {
      const exerciseDocRef = doc(this.firestore, `exercises/${exerciseId}`);

      // Update only the fields provided in the exercise parameter, along with updatedAt
      await updateDoc(exerciseDocRef, {
        ...exercise,
        updatedAt: serverTimestamp(), // Update the timestamp to reflect the modification time
      });
    } catch (error) {
      if (error instanceof Error) {
        this.errorService.showError(
          `Failed to update exercise: ${error.message}`
        );
      } else {
        this.errorService.showError(
          'Failed to update exercise due to an unexpected error'
        );
      }
    }
  }

  async getLatestExerciseHistoryByExerciseId(
    exerciseId: string
  ): Promise<ExerciseHistory | undefined> {
    try {
      const userId = await this.requireCurrentUserId();
      // Define the collection reference
      const exerciseHistoriesRef = collection(
        this.firestore,
        'exerciseHistories'
      );

      // Construct the query: filter by exerciseId, order by createdAt descending, and limit to 1
      const q = query(
        exerciseHistoriesRef,
        where('userId', '==', userId),
        where('exerciseId', '==', exerciseId),
        orderBy('createdAt', 'desc'),
        limit(1)
      );

      // Execute the query
      const querySnapshot = await getDocs(q);

      // Check if we got any results
      if (!querySnapshot.empty) {
        // Get the first document since it's the most recent one
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...(doc.data() as ExerciseHistory) }; // Return the exercise data
      } else {
        return undefined; // Return undefined if no document is found
      }
    } catch (error) {
      if (error instanceof Error) {
        this.errorService.showError(
          `Failed to fetch exercise history 1: ${error.message}`
        );
      } else {
        this.errorService.showError(
          'Failed to fetch exercise history due to an unexpected error'
        );
      }
      return undefined; // Return undefined to maintain function return type consistency
    }
  }

  async getLatestExerciseHistoryByExerciseIdAndPlan(
    exerciseId: string,
    planId: string
  ): Promise<ExerciseHistory | undefined> {
    try {
      const userId = await this.requireCurrentUserId();
      // Define the collection reference
      const exerciseHistoriesRef = collection(
        this.firestore,
        'exerciseHistories'
      );

      // Construct the query: filter by exerciseId and planId, order by createdAt descending, and limit to 1
      const q = query(
        exerciseHistoriesRef,
        where('userId', '==', userId),
        where('exerciseId', '==', exerciseId),
        where('activePlanId', '==', planId),
        orderBy('createdAt', 'desc'),
        limit(1)
      );

      // Execute the query
      const querySnapshot = await getDocs(q);

      // Check if we got any results
      if (!querySnapshot.empty) {
        // Get the first document since it's the most recent one
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...(doc.data() as ExerciseHistory) }; // Return the exercise data
      } else {
        return undefined; // Return undefined if no document is found
      }
    } catch (error) {
      if (error instanceof Error) {
        this.errorService.showError(
          `Failed to fetch exercise history: ${error.message}`
        );
      } else {
        this.errorService.showError(
          'Failed to fetch exercise history due to an unexpected error'
        );
      }
      return undefined; // Return undefined to maintain function return type consistency
    }
  }

  async getExerciseHistoryWithHighestWeightAndReps(
    exerciseId: string
  ): Promise<ExerciseHistory | undefined> {
    try {
      const userId = await this.requireCurrentUserId();
      const exerciseHistoriesRef = collection(
        this.firestore,
        'exerciseHistories'
      );

      // Construct the query: filter by exerciseId, order by weight descending then by reps descending
      const q = query(
        exerciseHistoriesRef,
        where('userId', '==', userId),
        where('exerciseId', '==', exerciseId),
        orderBy('highestWeight', 'desc'),
        orderBy('highestRep', 'desc'),
        limit(10) // Retrieve a reasonable number of top documents to find the max weight and corresponding max reps
      );

      // Execute the query
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // Assuming the first doc is the highest weight and within that the highest reps due to the query ordering
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...(doc.data() as ExerciseHistory) }; // Return the matching exercise history
      } else {
        return undefined; // Return undefined if no document is found
      }
    } catch (error) {
      if (error instanceof Error) {
        this.errorService.showError(
          `Failed to fetch exercise history 2: ${error.message}`
        );
      } else {
        this.errorService.showError(
          'Failed to fetch exercise history due to an unexpected error'
        );
      }
      return undefined; // Return undefined to maintain function return type consistency
    }
  }

  async getExerciseHistoriesByExerciseId(
    exerciseId: string
  ): Promise<ExerciseHistory[]> {
    try {
      const userId = await this.requireCurrentUserId();
      const exerciseHistoriesRef = collection(
        this.firestore,
        'exerciseHistories'
      );
      const q = query(
        exerciseHistoriesRef,
        where('userId', '==', userId),
        where('exerciseId', '==', exerciseId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const exerciseHistories: ExerciseHistory[] = [];

      querySnapshot.forEach((doc) => {
        const exerciseHistory = {
          id: doc.id,
          ...(doc.data() as ExerciseHistory),
        };
        exerciseHistories.push(exerciseHistory);
      });

      return exerciseHistories;
    } catch (error) {
      this.errorService.showError(
        `Failed to fetch exercise histories: ${
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred'
        }`
      );
      return []; // Return an empty array to maintain function return type consistency
    }
  }

  async removeExerciseHistory(exerciseHistoryId: string): Promise<void> {
    try {
      const exerciseHistoryDocRef = doc(
        this.firestore,
        `exerciseHistories/${exerciseHistoryId}`
      );
      await deleteDoc(exerciseHistoryDocRef);
    } catch (error) {
      this.errorService.showError(
        `Failed to remove exercise history: ${
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred'
        }`
      );
    }
  }

  async getExerciseByDbExerciseId(
    userId: string,
    dbExerciseId: string
  ): Promise<DbExercise | null> {
    try {
      const exercisesColRef = collection(this.firestore, 'exercises');
      const q = query(
        exercisesColRef,
        where('userId', '==', userId),
        where('dbExerciseId', '==', dbExerciseId),
        limit(1)
      );

      const querySnapshot = await getDocs(q);
      let exercise: DbExercise | null = null;
      querySnapshot.forEach((doc) => {
        exercise = doc.data() as DbExercise;
        exercise.id = doc.id;
      });
      return exercise;
    } catch (error) {
      this.errorService.showError(
        `Failed to fetch exercise: ${
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred'
        }`
      );
      return null; // Return null to indicate no exercise found or error occurred
    }
  }

  async getExerciseHistories(
    options: ExerciseHistoryQueryOptions
  ): Promise<ExerciseHistory[]>;
  /** @deprecated Prefer the typed options overload. */
  async getExerciseHistories(
    userId: string,
    timespan: number
  ): Promise<ExerciseHistory[]>;
  async getExerciseHistories(
    optionsOrUserId: ExerciseHistoryQueryOptions | string,
    timespan?: number
  ): Promise<ExerciseHistory[]> {
    try {
      const options =
        typeof optionsOrUserId === 'string'
          ? this.legacyHistoryQuery(optionsOrUserId, timespan)
          : optionsOrUserId;

      if (!options.userId) throw new Error('A user id is required.');
      if (options.scope.type === 'mesocycle' && !options.scope.mesocycleId) {
        throw new Error('A mesocycle id is required for mesocycle scope.');
      }
      this.assertValidDateRange(options.dateRange);

      const exerciseHistoriesRef = collection(
        this.firestore,
        'exerciseHistories'
      );
      const constraints: QueryConstraint[] = [
        where('userId', '==', options.userId),
      ];

      if (options.scope.type === 'mesocycle') {
        constraints.push(
          where('mesocycleId', '==', options.scope.mesocycleId)
        );
      }
      if (options.dateRange?.from) {
        constraints.push(
          where('createdAt', '>=', Timestamp.fromDate(options.dateRange.from))
        );
      }
      if (options.dateRange?.to) {
        constraints.push(
          where('createdAt', '<=', Timestamp.fromDate(options.dateRange.to))
        );
      }
      constraints.push(orderBy('createdAt', 'desc'));

      const q = query(exerciseHistoriesRef, ...constraints);

      const querySnapshot = await getDocs(q);
      const exerciseHistories: ExerciseHistory[] = [];

      querySnapshot.forEach((doc) => {
        const exerciseHistory = {
          id: doc.id,
          ...(doc.data() as ExerciseHistory),
        };
        exerciseHistories.push(exerciseHistory);
      });

      // Firestore equality does not match a missing field, so legacy
      // unassigned records must be selected from the owner-scoped result.
      return exerciseHistories.filter((history) =>
        matchesStatisticsScope(history, options.scope)
      );
    } catch (error) {
      console.log(error);
      this.errorService.showError(
        `Failed to fetch exercise histories: ${
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred'
        }`
      );
      return [];
    }
  }

  private legacyHistoryQuery(
    userId: string,
    timespan?: number
  ): ExerciseHistoryQueryOptions {
    if (timespan === undefined || !Number.isFinite(timespan) || timespan < 0) {
      throw new Error('A non-negative statistics timespan is required.');
    }
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - timespan);
    return {
      userId,
      scope: { type: 'all' },
      dateRange: { from: startDate },
    };
  }

  private assertValidDateRange(dateRange?: StatisticsDateRange): void {
    if (!dateRange) return;
    if (dateRange.from && Number.isNaN(dateRange.from.getTime())) {
      throw new Error('Statistics start date is invalid.');
    }
    if (dateRange.to && Number.isNaN(dateRange.to.getTime())) {
      throw new Error('Statistics end date is invalid.');
    }
    if (
      dateRange.from &&
      dateRange.to &&
      dateRange.from.getTime() > dateRange.to.getTime()
    ) {
      throw new Error('Statistics start date must not be after end date.');
    }
  }

  private async requireCurrentUserId(): Promise<string> {
    // A route can be created before Firebase restores persisted Google auth.
    // `authState()` then emits an initial null even though the user is already
    // signed in.  Waiting for the first non-null user prevents an empty
    // exercise-history view caused by that startup race.
    if (this.auth.currentUser) return this.auth.currentUser.uid;
    const user = await firstValueFrom(
      authState(this.auth).pipe(
        filter((candidate): candidate is NonNullable<typeof candidate> => !!candidate),
        take(1)
      )
    );
    return user.uid;
  }
}
